import { useEffect, useRef, useState } from 'react';

/**
 * ListShell — a status filter strip and its table as ONE card.
 *
 * The problem it solves: on the four Uniform Management list pages the status
 * strip and the `<Table>` were unrelated siblings. The table was already a
 * self-contained card (1px border, 14px radius, `overflow: hidden` —
 * `theme-overrides.css`), so the strip floated above it on a different left
 * edge (0 vs. the table's 20px cell inset) and its bottom hairline ran the
 * full container width before terminating in mid-air against the card's
 * curving top-left corner. Two objects, no shared geometry, and nothing
 * explaining the offset.
 *
 * The fix is markup, not a token: put the strip *inside* the card as its
 * header row. One bordered surface, one left edge at 20px (exactly
 * `Table.cellPaddingInline`), and the strip's hairline doubling as the table
 * header's top border — one line doing two jobs. Pagination moves into the
 * same card as a footer.
 *
 * ## Why this is not a tablist
 *
 * It used to be an antd `<Tabs>`, and that was an accessibility lie. The
 * table renders as a *sibling* of the strip, not inside a tab panel — that
 * part is deliberate, because tab panes would remount the table on every
 * status change and throw away its scroll position, and the page, not the
 * shell, owns the data fetch. But it left `role="tablist"` pointing at panels
 * that were empty and CSS-hidden, while the content those tabs actually
 * governed was an unassociated element outside the widget. To a screen reader
 * the tabs controlled nothing, and changing the filter announced nothing.
 *
 * Rather than fake the association (`aria-controls` at a table that is not a
 * `tabpanel`, on tabs that do not own it), the semantics now match the
 * behaviour: this is a group of toggle buttons that filter a list. Each is a
 * plain `<button aria-pressed>`, so every one is tabbable and activates on
 * Enter/Space — no roving tabindex and no arrow-key contract to implement and
 * get wrong, which a real `tablist` or `radiogroup` would both oblige. The
 * result of the change is reported through a polite live region instead.
 *
 * Usage — the caller keeps its own state, columns and data fetch:
 *
 *   <ListShell
 *     filters={filterItems}
 *     activeKey={statusFilter}
 *     onFilterChange={setStatusFilter}
 *     resultCount={pageSettings.total}
 *     loading={loading}
 *   >
 *     <Table ... />
 *   </ListShell>
 *
 * @param {Array<{key: string, label: React.ReactNode, count?: number}>} filters
 *   Filter definitions. `count` is a **reserved slot**: none of the list
 *   endpoints returns per-status counts today (the API sends `count` for the
 *   active filter only), so every call site omits it and no badge renders.
 *   When a `status_counts` aggregate lands, passing the number is the whole
 *   change — the geometry and the badge styling already exist.
 * @param {string} activeKey Currently selected filter key ('' for "All").
 * @param {(key: string) => void} onFilterChange
 * @param {string} [label] Accessible name for the group. Override when a page
 *   filters by something other than status.
 * @param {number} [resultCount] Total rows for the active filter, announced
 *   after the refetch settles. Omit and only the filter name is announced.
 * @param {boolean} [loading] The caller's fetch state. Used to time the
 *   announcement; see `useEffect` below.
 * @param {string} [className] Extra classes on the shell element.
 * @param {React.ReactNode} children The `<Table>` (and anything else that
 *   belongs inside the card).
 */
const ListShell = ({
  filters = [],
  activeKey,
  onFilterChange,
  label = 'Filter by status',
  resultCount,
  loading = false,
  className = '',
  children,
}) => {
  const [announcement, setAnnouncement] = useState('');
  // Only a *user-initiated* filter change is worth announcing. Without this
  // the live region would also fire on first paint and on every pagination
  // refetch, which is noise, not information.
  const pendingRef = useRef(false);
  const prevLoadingRef = useRef(loading);

  const handleSelect = (key) => {
    if (key === activeKey) return;
    pendingRef.current = true;
    onFilterChange?.(key);
  };

  useEffect(() => {
    // `loading` going true→false is the only reliable signal that
    // `resultCount` belongs to the filter the user just picked. Announcing on
    // the `activeKey` change alone would read out the *previous* filter's
    // total, because the parent re-renders with the new key one tick before
    // it starts the fetch.
    const settled = prevLoadingRef.current && !loading;
    prevLoadingRef.current = loading;

    if (!pendingRef.current) return;
    if (typeof resultCount === 'number' && !settled) return;

    pendingRef.current = false;
    const active = filters.find((f) => f.key === activeKey);
    const name = typeof active?.label === 'string' ? active.label : 'Filter';
    setAnnouncement(
      typeof resultCount === 'number'
        ? `${name}: ${resultCount} ${resultCount === 1 ? 'result' : 'results'}`
        : `${name} selected`
    );
  }, [activeKey, resultCount, loading, filters]);

  return (
    <div className={`list-shell ${className}`.trim()}>
      <div className="list-shell-filters" role="group" aria-label={label}>
        {filters.map(({ key, label: filterLabel, count }) => (
          <button
            key={key}
            type="button"
            className="list-shell-filter"
            aria-pressed={key === activeKey}
            onClick={() => handleSelect(key)}
          >
            {filterLabel}
            {typeof count === 'number' && (
              <em className="list-shell-filter-count">{count}</em>
            )}
          </button>
        ))}
      </div>
      {children}
      {/* Outside the group so it is not read as part of the control, and
          `role="status"` as well as `aria-live` because Safari/VoiceOver is
          more dependable with the role. */}
      <span className="list-shell-announcer" role="status" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
};

export default ListShell;
